// Type definitions for template
import _ from "lodash"
import { OptionsRegistry } from "@cmodules/checklist/assets/data/types/vm/template-builder/types"

class CustomUnitVM {
  key: string;
  name: string;
  symbol: string;
  created_at: string;

  constructor(data: Partial<CustomUnitVM> = {}) {
    this.key = data?.key || ''
    this.name = data?.name || ''
    this.symbol = data?.symbol || ''
    this.created_at = data?.created_at || ''
  }
}

class CommonItemFields {
  id: string;
  collapsed: boolean;
  title: string;
  parent_id: string | null;
  base_id: string;
  reference_key: string | null;

  /**
   * @description The base id of the main item inside the global_item(sub_template).
   * @default null
   * */
  global_item_base_id: string | null;
  visible: boolean;
  depth: number | null;

  constructor(data: Partial<CommonItemFields> = {}) {
    this.id = data?.id || ""
    this.collapsed = data?.collapsed ?? false
    this.title = data?.title || ""
    this.parent_id = data?.parent_id ?? null
    this.base_id = data?.base_id || ""
    this.reference_key = data?.reference_key ?? null
    this.global_item_base_id = data?.global_item_base_id ?? null
    this.visible = data?.visible ?? true
    this.depth = data?.depth ?? null
  }
}

type NonNullItemType = keyof typeof OptionsRegistry
type ItemType = NonNullItemType

type OptionsOf<T extends ItemType> =
  T extends null ? null : InstanceType<typeof OptionsRegistry[Extract<T, NonNullItemType>]>

// Optional: build a discriminated union from the registry (for external typing if needed)
type RegistryBasedItemUnion =
  | { [K in NonNullItemType]: { type: K; options: InstanceType<typeof OptionsRegistry[K]> } }[NonNullItemType]
// | { type: null; options: null }


// Define the ItemVM class WITHOUT generics to avoid constructor narrowing issues
class ItemVM<T extends ItemType> extends CommonItemFields {
  type: T
  options: OptionsOf<T>

  constructor(
    data: Partial<CommonItemFields> & Partial<RegistryBasedItemUnion> & { type?: T } = {}
  ) {
    super(data)

    this.type = ((data as any)?.type ?? null) as T

    if (this.type === null) {
      this.options = null as OptionsOf<T>
    } else {
      const t = this.type as NonNullItemType
      const Ctor = OptionsRegistry[t]
      const raw = (data as any)?.options
      this.options = (raw instanceof Ctor ? raw : new Ctor(raw)) as OptionsOf<T>
    }
  }

  // Helper to build a new VM from an existing one by copying only CommonItemFields
  private convertTo<K extends NonNullItemType>(
    to: K,
    options?: ConstructorParameters<typeof OptionsRegistry[K]>[0] | InstanceType<typeof OptionsRegistry[K]>
  ): ItemVM<K> {
    const common: Partial<CommonItemFields> = {
      id: this.id,
      collapsed: this.collapsed,
      title: this.title,
      parent_id: this.parent_id,
      base_id: this.base_id,
      reference_key: this.reference_key,
      visible: this.visible,
      depth: this.depth
    }
    return new ItemVM<K>({ ...common, type: to, options } as any)
  }

  static cloneToType<K extends NonNullItemType>(src: AnyItemVM, to: K): ItemVM<K> {
    const TargetCtor = OptionsRegistry[to]
    // Start with target defaults
    const targetOptions = new TargetCtor()
    const srcOptions = (src as any)?.options ?? {}

    // Copy intersection of option keys
    for (const key of Object.keys(srcOptions)) {
      if (key in targetOptions) {
        try {
          ; (targetOptions as any)[key] = (srcOptions as any)[key]
        } catch {
          // ignore assignment errors for read-only or mismatched fields
        }
      }
    }

    const instance = new ItemVM(src)
    return instance.convertTo(to, targetOptions)
  }


  static convert_AnyItemVM_toItemVM<T extends AnyItemVM['type']>(
    data: Extract<AnyItemVM, { type: T }>
  ): AnyItemVM {
    return new ItemVM<T>(data as any) as AnyItemVM
  }

  static generateEmptyObjectAsAnyItemVM(): AnyItemVM {
    return {} as AnyItemVM
  }

  static buildAnyItemVM(
    data: Extract<AnyItemVM, { type: AnyItemVM['type'] }>
  ): AnyItemVM {
    return new ItemVM(data as any) as AnyItemVM
  }

  static convert_ItemVM_ToAnyItemVM<U extends NonNullItemType>(vm: ItemVM<U>): AnyItemVM {
    return _.cloneDeep(vm) as unknown as AnyItemVM
  }
}

type AnyItemVM =
  | { [K in NonNullItemType]: ItemVM<K> }[NonNullItemType];

export type { AnyItemVM }
export { ItemVM, CustomUnitVM }